var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { Button, Link, LoadingSpinner, Text, TextInput } from '@paypalcorp/pp-react';
import { getProp, isMobile, isSafari, isSafariKeychainPrefilledField } from '../common/utils';
import { OTP_CODE, PASSWORD, PUBLIC_CREDENTIAL, SEND_OTP } from '../common/views';
import { initializeAsync, initializeSync } from '../common/fn';
import PhoneInput from '../common/PhoneInput';
import { fetchPhoneCodeList, getStrippedPhoneNumber, looksLikeAnEmail } from '../common/PhoneInput/utils';
import { LOCAL, PREFERRED_LOGIN_TYPES, STAGE_URL } from './constants';
const { PASSWORD_LOGIN } = PREFERRED_LOGIN_TYPES;
export default ({ authEventHandler, authProviderContext = { fnData: {}, cookiedUser: {} }, csrf, csrfNonce, localizedContent, logger, options = {}, publicCredential = '', publicCredentialLookup, setChallengeId, setIsAutoOtp, setIsAaeUser, setView, setIsOtpSent, updateAfterPublicCredentialLookup, isCookiedUser, forgetUser, changeUser, }) => {
    // A public credential could be passed on from the parent,
    // hence consider the user inputted public credential as "local" to this component
    const [localPublicCredential, setLocalPublicCredential] = useState(publicCredential);
    const [publicCredentialFieldError, setLocalPublicCredentialFieldError] = useState('');
    const [selectedCountryCode, setSelectedCountryCode] = useState('US +1');
    const [isPhoneNumber, setIsPhoneNumber] = useState(false);
    // Cookied users with truthy public credential will always initiate public credential call
    // For this use case, we must initiate showSpinner with boolean value of true before the first render.
    const [showSpinner, setShowSpinner] = useState(!!(isCookiedUser && publicCredential));
    const [phoneCodeList, setPhoneCodeList] = useState(null);
    const [showPageLevelError, setShowPageLevelError] = useState('');
    const [processingPublicCredentialLookup, setProcessingPublicCredentialLookup] = useState(false);
    const [showInputField, setShowInputField] = useState(!showSpinner);
    const [citStartTime, setCitStartTime] = useState('');
    useEffect(() => {
        initializeAsync(authProviderContext.fnData);
        initializeSync(authProviderContext.fnData);
        setIsAaeUser(false);
        setCitStartTime(Date.now());
        if (publicCredential && isCookiedUser) {
            handlePublicCredentialSubmit();
            // If this is a cookied user, then this view will be mounted with a publicCredential
            logger.track('pc_cookied');
        }
        else {
            // We need to fetch phone code list here (instead of within the PhoneInput component)
            // because we dont want to do this every time the PhoneInput get s remounted as user
            // types in numbers v/s string
            fetchPhoneCodeList(setPhoneCodeList);
            logger.track('pc_rendered');
            if (!changeUser) {
                authEventHandler({
                    event: 'initialPageRendered',
                    data: {
                        view: PUBLIC_CREDENTIAL,
                    },
                });
            }
        }
        // The current view is a special view, since it can be the first view to be rendered,
        // it can be rendered without a csrf in which case we need to use the csrfNonce
        // (it can also be rendered "with" a csrf however, for eg user coming back from AAE page)
        logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
    }, []);
    const handlePublicCredentialSubmit = (e) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        e && e.preventDefault();
        const startTime = Date.now();
        // Determine if its password prefill via keychain saved email/pwd
        const isPasswordPrefilled = isSafariKeychainPrefilledField(document.querySelector('#prefillPwdInput'));
        // Prevent proceeding in case spinner is shown (implying a network request is underway)
        if (processingPublicCredentialLookup) {
            return;
        }
        if (!isCookiedUser) {
            logger.addToFlowContext({
                pub_cred_type: isPhoneNumber ? 'phone' : 'email',
            });
        }
        // If this is not a cookied user, then this view wouldnt be mounted with a publicCredential
        // and hence we can presume that at this point, the user has entered & submitted a value manually
        if (!publicCredential) {
            logger.track(`pc_entered_${isPhoneNumber ? 'phone' : 'email'}`);
            authEventHandler({
                event: 'publicCredentialSubmitted',
            });
        }
        const countryCode = selectedCountryCode.split(/\s/)[1];
        let validPhoneNumber;
        if (!isCookiedUser) {
            if (!isPhoneNumber) {
                if (localPublicCredential.trim() === '') {
                    setShowInputField(true);
                    setShowSpinner(false);
                    setLocalPublicCredentialFieldError(localizedContent('required'));
                    return;
                }
                if (!localPublicCredential.match(/\S+@\S+\.\S+/)) {
                    logger.track('pc_invalid_email_entered');
                    setShowInputField(true);
                    setShowSpinner(false);
                    setLocalPublicCredentialFieldError(localizedContent('invalidEmailError'));
                    return;
                }
            }
            else {
                validPhoneNumber = getStrippedPhoneNumber(localPublicCredential, countryCode);
                if (!validPhoneNumber) {
                    logger.track('pc_invalid_phone_entered');
                    setShowInputField(true);
                    setShowSpinner(false);
                    setLocalPublicCredentialFieldError(localizedContent('invalidEmailError'));
                    return;
                }
            }
        }
        validPhoneNumber = getStrippedPhoneNumber(localPublicCredential, countryCode);
        setShowSpinner(true);
        setProcessingPublicCredentialLookup(true);
        logger.track('lookup_started');
        const publicCredentialType = validPhoneNumber || ((_a = authProviderContext === null || authProviderContext === void 0 ? void 0 : authProviderContext.cookiedUser) === null || _a === void 0 ? void 0 : _a.maskedPhoneNumber) ? 'PHONE' : 'EMAIL';
        // in case of phone number, prefix the country code followed by a space
        const phoneOrEmail = validPhoneNumber ? countryCode + ' ' + validPhoneNumber : localPublicCredential;
        const response = yield publicCredentialLookup({
            publicCredential: phoneOrEmail,
            publicCredentialType,
            maskedPhoneNumber: (_b = authProviderContext === null || authProviderContext === void 0 ? void 0 : authProviderContext.cookiedUser) === null || _b === void 0 ? void 0 : _b.maskedPhoneNumber,
        });
        logger.track('lookup_completed');
        if (response === 'invalid_ctxId') {
            logger.track('gql_request_forbidden');
        }
        // XO Lite apps do not have the ngRL JS SDK loaded,
        // hence a captcha response can go unhandled
        // the following is a poor way to handle it so that XO can fallback
        const extensions = getProp(response, 'extensions', null);
        if (!extensions) {
            authEventHandler({
                event: response === 'invalid_ctxId' ? 'invalidCtxId' : 'unknownResponse',
                data: {
                    response,
                },
            });
        }
        updateAfterPublicCredentialLookup({ response, publicCredential: phoneOrEmail, publicCredentialType });
        setProcessingPublicCredentialLookup(false);
        setShowSpinner(false);
        setShowInputField(true);
        const getLookupResponse = getProp(response, 'data.login.lookup', {});
        const { preferredLogin, otpResponse, loginPreference } = getLookupResponse;
        if (Array.isArray(preferredLogin)) {
            // User is on file
            // and they are a phone only user (because they submitted a phone which resulted in an empty preferred login array)
            if (preferredLogin.length === 0 && publicCredentialType === 'PHONE') {
                authEventHandler({
                    event: 'phoneUser',
                    data: {
                        publicCredential: phoneOrEmail,
                        publicCredentialType,
                    },
                });
                isCookiedUser && authEventHandler({ event: 'initialPageRendered', data: { view: PASSWORD } });
                logger.track(`cpl_cit_${PUBLIC_CREDENTIAL}|${JSON.stringify({
                    start: citStartTime,
                    tt: Date.now() - citStartTime,
                    login_experience: 'Process Next Btn Click',
                })}`);
                logger.track(`cpl_lookup|${JSON.stringify({
                    start: startTime,
                    tt: Date.now() - startTime,
                    login_experience: 'Hybrid Pwd Login',
                })}`);
                setShowInputField(false);
                setView(PASSWORD);
                return;
            }
        }
        else {
            // User is not on file and they had entered a phone number to start with
            // which cannot be on file
            if (publicCredentialType === 'PHONE') {
                setShowPageLevelError(true);
                setLocalPublicCredential('');
                setIsPhoneNumber(false);
                logger.track(`cpl_lookup|${JSON.stringify({
                    start: startTime,
                    tt: Date.now() - startTime,
                    login_experience: 'Phone login error',
                })}`);
                return;
            }
            else {
                // Since this can be the first network request, it s possible that csrf is not yet hydrated
                // hence grab the csrf from the response for the forgetUser scenario for a uncookied non existent user
                const updatedCsrf = response._csrf;
                const updatedSessionID = response._sessionID;
                forgetUser({ updatedCsrf, updatedSessionID });
                if (!isCookiedUser) {
                    authEventHandler({
                        event: 'userNotFound',
                        data: {
                            publicCredential: phoneOrEmail,
                            publicCredentialType,
                        },
                    });
                }
                logger.track(`cpl_lookup|${JSON.stringify({
                    start: startTime,
                    tt: Date.now() - startTime,
                    login_experience: 'User not found',
                })}`);
                // this implies nothing will happen on the client as we expect parent app to unmount IWC
                return;
            }
        }
        const { challenges = [] } = otpResponse || {};
        if (preferredLogin.length > 0 && challenges.length > 0 && preferredLogin.includes('OTP') && !isPhoneNumber) {
            // User is eligible for OTP
            const isAutoOtp = challenges.find((challenge) => challenge.notified);
            setIsAutoOtp(isAutoOtp ? isAutoOtp.notified : false);
            setChallengeId(isAutoOtp ? isAutoOtp.challengeId : challenges[0].challengeId);
            let pageView;
            if (isAutoOtp) {
                pageView = OTP_CODE;
                setIsOtpSent(true);
                logger.track(`otp_rendered|${JSON.stringify({
                    login_method: 'otp',
                    login_variant: 'auto sms',
                })}`);
            }
            else if (isPasswordPrefilled && isSafari()) {
                pageView = PASSWORD;
            }
            else {
                pageView = loginPreference === PASSWORD_LOGIN ? PASSWORD : SEND_OTP;
            }
            isCookiedUser && authEventHandler({ event: 'initialPageRendered', data: { view: pageView } });
            let login_experience = isAutoOtp ? 'Auto sms otp login' : pageView === PASSWORD ? 'Hybrid Pwd Login' : 'send otp';
            const renderStartTime = publicCredential && isCookiedUser ? (_c = window === null || window === void 0 ? void 0 : window.performance) === null || _c === void 0 ? void 0 : _c.timeOrigin : startTime;
            logger.track(`cpl_lookup|${JSON.stringify({
                start: renderStartTime,
                tt: Date.now() - renderStartTime,
                login_experience,
            })}`);
            logger.track(`cpl_cit_${PUBLIC_CREDENTIAL}|${JSON.stringify({
                start: citStartTime,
                tt: Date.now() - citStartTime,
                login_experience: 'Process Next Btn Click',
            })}`);
            setShowInputField(false);
            setView(pageView);
        }
        else {
            isCookiedUser && authEventHandler({ event: 'initialPageRendered', data: { view: PASSWORD } });
            logger.track(`cpl_lookup|${JSON.stringify({
                start: startTime,
                tt: Date.now() - startTime,
                login_experience: 'Hybrid Pwd Login',
            })}`);
            logger.track(`cpl_cit_${PUBLIC_CREDENTIAL}|${JSON.stringify({
                start: citStartTime,
                tt: Date.now() - citStartTime,
                login_experience: 'Process Next Btn Click',
            })}`);
            setShowInputField(false);
            setView(PASSWORD);
        }
    });
    const handleCountryCodeChange = (e) => {
        setSelectedCountryCode(e.target.value);
    };
    const handlePublicCredentialChange = (e) => {
        if (publicCredentialFieldError !== '')
            setLocalPublicCredentialFieldError('');
        setLocalPublicCredential(e.target.value);
        const isEmail = looksLikeAnEmail(e.target.value);
        setIsPhoneNumber(isEmail ? false : true);
    };
    // If inputField turned into a component, there is an issue with keychain popup on safari
    // The issue is that the keychain popup will flicker(as if its being removed and added back really quick)
    const inputField = isPhoneNumber ? (React.createElement(PhoneInput, { handleCountryCodeChange: handleCountryCodeChange, handlePublicCredentialChange: handlePublicCredentialChange, phoneCodeList: phoneCodeList, publicCredential: localPublicCredential, publicCredentialFieldError: publicCredentialFieldError, selectedCountryCode: selectedCountryCode, label: localizedContent('emailOrMobile') })) : (React.createElement(TextInput, { "data-testid": "input-email", name: "email", id: "email", type: "email", label: localizedContent('emailOrMobile'), value: localPublicCredential, onChange: handlePublicCredentialChange, error: !!publicCredentialFieldError, errorText: publicCredentialFieldError, autoComplete: "username", tabIndex: "1", autoFocus: !isMobile() }));
    const handlePrivateCredentialChange = (e) => {
        const container = document.querySelector('#iwcContainer');
        const pwdinput = document.createElement('input');
        pwdinput.type = 'hidden';
        pwdinput.id = 'prefillPwdInput';
        pwdinput.value = e.target.value;
        container.appendChild(pwdinput);
    };
    return (React.createElement(React.Fragment, null,
        showSpinner && (React.createElement("div", { className: "overlay-spinner", style: { opacity: isCookiedUser ? 1 : 0.7 } },
            React.createElement(LoadingSpinner, { screenReaderText: localizedContent('loading'), size: "xl" }))),
        React.createElement("form", { onSubmit: handlePublicCredentialSubmit, id: "publicCredentialContainer", "data-testid": "public-credential-container" },
            React.createElement(Text, { size: "xl2", className: "heading", id: "publicCredentialHeading", "data-testid": "public-credential-heading" }, localizedContent('genericHeading')),
            React.createElement("div", null,
                React.createElement(Text, { size: "md", style: { marginBottom: '25px' }, id: "publicCredentialSubHeading", "data-testid": "public-credential-sub-heading" }, localizedContent('publicCredentialSubHeading')),
                showPageLevelError && (React.createElement("div", { role: "alert", className: "ulNotification-warning", id: "publicCredentialError", "data-testid": "public-credential-error" }, localizedContent('useEmail'))),
                showInputField && inputField,
                !isCookiedUser && (React.createElement("div", { className: "hiddenElement" },
                    React.createElement("input", { id: "password", type: "password", autoComplete: "current-password", name: "login_password", onChange: handlePrivateCredentialChange }))),
                React.createElement("div", { style: { margin: '10px 0', textAlign: 'left' } },
                    React.createElement(Link, { href: "#", className: "secondaryLink", id: "linkForgotEmail", "data-testid": "link-forgot-email", onClick: (e) => {
                            e.preventDefault();
                            logger.track('emr_clicked');
                            logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
                            if (options.signinUrl) {
                                location.assign(`${options.env === LOCAL ? STAGE_URL : ''}/authflow/email-recovery/?contextId=${authProviderContext.fnData.fnSessionId}&redirectUri=${encodeURIComponent(encodeURIComponent(options.signinUrl))}&sso=true`);
                                return;
                            }
                            authEventHandler({
                                event: 'userForgotEmail',
                            });
                        }, tabIndex: "2" }, localizedContent('forgotEmail')))),
            React.createElement("div", { style: { marginTop: '20px' } },
                React.createElement(Button, { type: "submit", style: { width: '100%' }, id: "buttonSubmitEmail", "data-testid": "button-submit-email", tabIndex: "3" }, localizedContent('next'))))));
};
